/**
 * Composição/parsing de chaves do `SelectionStorageAdapter`.
 *
 * Invariantes:
 * - Chaves são tab-scoped (sessionStorage), sempre prefixadas pelo `user_id`
 *   (JWT `sub`, assumido UUID — sem `:`).
 * - Hierarquia: organização → conta (inclui org) → assistente (inclui org + conta).
 * - Sufixos com palavras completas casam a linguagem ubíqua do projeto.
 */

export const SELECTION_KEY_SUFFIX = {
    ORGANIZATION: 'organization',
    ACCOUNT: 'account',
    ASSISTANT: 'assistant',
} as const;

export function orgKey(userId: string): string {
    return `${userId}:${SELECTION_KEY_SUFFIX.ORGANIZATION}`;
}

export function accountKey(userId: string, orgId: string): string {
    return `${userId}:${orgId}:${SELECTION_KEY_SUFFIX.ACCOUNT}`;
}

export function assistantKey(userId: string, orgId: string, accountId: string): string {
    return `${userId}:${orgId}:${accountId}:${SELECTION_KEY_SUFFIX.ASSISTANT}`;
}

/**
 * Retorna true se a chave corresponde a um dos três formatos do adapter:
 * - `{user}:organization`
 * - `{user}:{org}:account`
 * - `{user}:{org}:{account}:assistant`
 *
 * Usado por `purgeOtherUsers`, `purgeOrphans` e `clearAllForCurrentUser`
 * para nunca remover chaves de terceiros (libs, sentry, etc) que casualmente
 * contenham `:`.
 */
export function isAdapterKey(key: string): boolean {
    const parts = key.split(':');
    if (parts.length === 2) return parts[1] === SELECTION_KEY_SUFFIX.ORGANIZATION;
    if (parts.length === 3) return parts[2] === SELECTION_KEY_SUFFIX.ACCOUNT;
    if (parts.length === 4) return parts[3] === SELECTION_KEY_SUFFIX.ASSISTANT;
    return false;
}

export type ParsedKey =
    | { kind: 'organization'; userId: string }
    | { kind: 'account'; userId: string; orgId: string }
    | { kind: 'assistant'; userId: string; orgId: string; accountId: string };

export function parseKey(key: string): ParsedKey | null {
    const parts = key.split(':');
    if (parts.length === 2 && parts[1] === SELECTION_KEY_SUFFIX.ORGANIZATION) {
        return { kind: 'organization', userId: parts[0] };
    }
    if (parts.length === 3 && parts[2] === SELECTION_KEY_SUFFIX.ACCOUNT) {
        return { kind: 'account', userId: parts[0], orgId: parts[1] };
    }
    if (parts.length === 4 && parts[3] === SELECTION_KEY_SUFFIX.ASSISTANT) {
        return {
            kind: 'assistant',
            userId: parts[0],
            orgId: parts[1],
            accountId: parts[2],
        };
    }
    return null;
}
